import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router";

import { signInUrl } from "../auth/session";
import { usePageTitle } from "../lib/hooks";
import { Notice } from "../ui/Notice";
import "./signin.css";

const ERRORS: Record<string, string> = {
  expired: "Signing in took too long. Please try again.",
  cancelled: "Sign-in was cancelled.",
  account_disabled: "Your support desk account is disabled. Please contact IT.",
  email_belongs_to_another_account: "This email address is linked to a different Microsoft account. Please contact IT.",
};
const GENERIC_ERROR = "We couldn't sign you in. Please try again, or contact IT if it keeps happening.";

// What people come for, under the headline (icons from the design).
const TOPICS = [
  ["/signin/icon-laptop.svg", "Laptops & devices"],
  ["/signin/icon-unlock.svg", "Accounts & access"],
  ["/signin/icon-message-check.svg", "Aspect apps"],
] as const;

// Chrome's zoom steps: the estimate below snaps to the nearest one.
const ZOOM_STEPS = [0.25, 0.33, 0.5, 0.67, 0.75, 0.8, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2, 2.5, 3, 4, 5];

// Windows and macOS display scales: a real browser zoom leaves one of these behind.
const DISPLAY_SCALES = [1, 1.25, 1.5, 1.75, 2, 2.25, 2.5, 3];

function near(value: number, target: number, tolerance: number): boolean {
  return Math.abs(value - target) / target <= tolerance;
}

/**
 * How far the page is zoomed: the window's width over the page's, snapped to a zoom step
 * (a window's frame makes 100% read as about 1.01), in or out.
 *
 * A side panel, docked dev tools or vertical tabs also narrow the page without any zoom, so a
 * reading only counts if the screen's pixel density agrees: zooming raises devicePixelRatio by
 * the same factor (leaving a normal display scale), a side panel doesn't.
 */
export function zoomLevel(outer: number, inner: number, pixelRatio = 1): number {
  if (!outer || !inner || !pixelRatio) return 1;
  const raw = outer / inner;
  const nearest = ZOOM_STEPS.reduce((best, step) => (Math.abs(step - raw) < Math.abs(best - raw) ? step : best), 1);
  if (nearest === 1 || !near(raw, nearest, 0.04)) return 1;
  const displayScale = pixelRatio / nearest;
  return DISPLAY_SCALES.some((scale) => near(displayScale, scale, 0.03)) ? nearest : 1;
}

// The decorations on the photo, each hidden on its own if it would clash (see below).
const DECORATIONS = [".signin-badge-call", ".signin-badge-access", ".signin-badge-devices", ".signin-resolved", ".signin-sparkle"];
const CLEARANCE = 8;

function overlaps(a: DOMRect, b: DOMRect): boolean {
  return a.left < b.right + CLEARANCE && a.right > b.left - CLEARANCE && a.top < b.bottom + CLEARANCE && a.bottom > b.top - CLEARANCE;
}

/**
 * The photo grows with the browser's zoom like the text does (CSS can't tell zooming from a
 * smaller window, so we estimate it). A badge or card that would run into the name, the
 * headline or the panel's edge (a short window, or zoomed right in) steps aside on its own.
 */
function useZoomedBrandPanel() {
  const page = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = page.current;
    if (!el) return;
    let frame = 0;
    function update() {
      if (!el) return;
      // --zoom grows the page when zoomed in; --zoom-raw (in or out) keeps the card's minimum
      // size from shrinking on its own when zoomed out (see signin.css).
      const zoom = zoomLevel(window.outerWidth, window.innerWidth, window.devicePixelRatio);
      el.style.setProperty("--zoom-raw", String(zoom));
      el.style.setProperty("--zoom", String(Math.max(1, zoom)));
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const panel = el.querySelector(".signin-brand")?.getBoundingClientRect();
        // Where the words actually are: the name, the headline's text and each topic chip
        // (their boxes run the full width of the panel).
        const text: DOMRect[] = [];
        const header = el.querySelector(".signin-header");
        if (header) text.push(header.getBoundingClientRect());
        const title = el.querySelector(".signin-title");
        if (title) {
          const range = document.createRange();
          range.selectNodeContents(title);
          text.push(range.getBoundingClientRect());
        }
        el.querySelectorAll(".signin-topic").forEach((chip) => text.push(chip.getBoundingClientRect()));
        for (const selector of DECORATIONS) {
          const item = el.querySelector(selector);
          if (!item || !panel) continue;
          const r = item.getBoundingClientRect();
          const outside = r.left < panel.left || r.right > panel.right || r.top < panel.top || r.bottom > panel.bottom;
          item.toggleAttribute("data-clash", outside || text.some((t) => overlaps(r, t)));
        }
      });
    }
    update();
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("resize", update);
      cancelAnimationFrame(frame);
    };
  }, []);
  return page;
}

function localPath(value: string | null): string {
  return value && value.startsWith("/") && !value.startsWith("//") ? value : "/";
}

/** Sign in: a duotone photo of the Aspect office with focus rings, and the Microsoft sign-in card. */
export function SignInPage() {
  usePageTitle("Sign in");
  const [params] = useSearchParams();
  const error = params.get("error");
  const signedOut = params.get("signed_out") === "1";
  const returnTo = localPath(params.get("return_to"));
  const page = useZoomedBrandPanel();

  return (
    <div className="signin" ref={page}>
      <section className="signin-brand" aria-label="About the support desk">
        {/* The photo, then (above the scrim) the rings and badges, on two identical stages scaled
            like a covering image, so they stay on the same spots of the photo at any size. */}
        <div className="signin-stage" aria-hidden="true">
          <img className="signin-photo" src="/signin/photo-agent.jpg" alt="" />
        </div>
        {/* On the panel, not the stage: the headline always sits on the dark part. */}
        <div className="signin-scrim" aria-hidden="true" />
        <div className="signin-stage signin-stage-marks" aria-hidden="true">
          <img className="signin-ring signin-ring-dashed" src="/signin/ring-dashed.png" alt="" width={430} height={430} />
          <img className="signin-ring signin-ring-outer" src="/signin/ring-outer.svg" alt="" width={560} height={560} />
          <img className="signin-accents" src="/signin/accents-panel.svg" alt="" width={744} height={884} />
          <span className="signin-badge signin-badge-call">
            <img src="/signin/icon-phone-call.svg" alt="" width={24} height={24} />
          </span>
          <span className="signin-badge signin-badge-devices">
            <img src="/signin/icon-setting.svg" alt="" width={22} height={22} />
          </span>
          <span className="signin-badge signin-badge-access">
            <img src="/signin/icon-lock.svg" alt="" width={22} height={22} />
          </span>
          <span className="signin-resolved">
            <span className="signin-resolved-tick">
              <img src="/signin/icon-check.svg" alt="" width={18} height={18} />
            </span>
            <span className="signin-resolved-text">
              <span className="signin-resolved-title">Ticket resolved</span>
              <span className="signin-resolved-meta">Laptop setup · 12 min</span>
            </span>
          </span>
          <img className="signin-sparkle" src="/signin/sparkle-tiny.svg" alt="" width={10} height={10} />
        </div>
        <div className="signin-header">
          <span className="signin-mark-tile">
            <img src="/signin/mark-tile.svg" alt="" width={22} height={22} />
          </span>
          <span className="signin-name">Aspect Support Desk</span>
        </div>
        <div className="signin-headline">
          <p className="signin-title">
            IT and app support
            <br />
            for <span className="signin-highlight">everyone</span> at Aspect.
          </p>
          <ul className="signin-topics" aria-label="Get help with">
            {TOPICS.map(([icon, label]) => (
              <li key={label} className="signin-topic">
                <img src={icon} alt="" width={16} height={16} />
                {label}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <main className="signin-side" id="main">
        <div className="signin-rings" aria-hidden="true">
          <img className="signin-side-ring signin-side-ring-200" src="/signin/ring-200.svg" alt="" width={400} height={400} />
          <img className="signin-side-ring signin-side-ring-300" src="/signin/ring-300-dashed.png" alt="" width={600} height={600} />
          <img className="signin-side-ring signin-side-ring-400" src="/signin/ring-400.svg" alt="" width={800} height={800} />
        </div>
        <div className="signin-card-wrap">
          <img className="signin-dots" src="/signin/dot-grid.svg" alt="" width={39} height={39} aria-hidden="true" />
          <div className="signin-card">
            <div className="signin-card-brand">
              <img className="signin-card-mark" src="/signin/mark.svg" alt="" width={40} height={40} />
              {/* On phones the photo panel (and its name) is hidden, so the card names the app. */}
              <span className="signin-card-name">Aspect Support Desk</span>
            </div>
            <h1 className="signin-card-title">Sign in</h1>
            <p className="signin-card-sub">Use your Aspect Microsoft account.</p>
            {error ? <Notice tone="error">{ERRORS[error] ?? GENERIC_ERROR}</Notice> : null}
            {signedOut && !error ? <Notice tone="success">You've signed out.</Notice> : null}
            <a className="signin-cta" href={signInUrl(returnTo)}>
              <MicrosoftLogo />
              Sign in with Microsoft
            </a>
            <hr className="signin-divider" />
            <p className="signin-help">
              <img src="/signin/icon-phone.svg" alt="" width={18} height={18} />
              <span>
                Trouble signing in? <a href="mailto:support@aspect.co.uk">Contact the IT team</a>
              </span>
            </p>
          </div>
        </div>
        <p className="signin-footer">© 2026 Aspect Maintenance Services Ltd · For Aspect staff only</p>
      </main>
    </div>
  );
}

/** Microsoft's four-square logo, coloured by the --ms-* tokens. */
function MicrosoftLogo() {
  return (
    <svg className="ms-logo" width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <rect className="ms-red" x="0" y="0" width="8.5" height="8.5" />
      <rect className="ms-green" x="9.5" y="0" width="8.5" height="8.5" />
      <rect className="ms-blue" x="0" y="9.5" width="8.5" height="8.5" />
      <rect className="ms-yellow" x="9.5" y="9.5" width="8.5" height="8.5" />
    </svg>
  );
}
